import './BetbookPostItem.less';

import clsx from 'clsx';
import { fromJS } from 'immutable';
import moment from 'moment';
import React, { useContext, useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import i18n from '@i18n';
import PopupNg from '@/ui/PopupNg';
import { fireGAEvent } from '@/analytics';
import BetbookBetBox from './BetbookBetBox';
import Button from '@/common/component/Button';
import Avatar from '@/common/components/Avatar';
import { addAnalysisToChat } from '@/betbook/handlers';
import { getConfigProperty } from '@/common/oldMobile';
import { nike1Bet$, nike1SportEvent$ } from '@/nikeone/subs';
import { BetbookContextProvider } from 'nike-ui/src/common/BetbookContext';
import { getPostTitle } from '@/common/forum';
import TicketDetail from '@/mytickets/detail/views/TicketDetail';
import BetbookPostTicket from '@/betbook/views/BetbookPostTicket';
import TicketDetailLoader from '@/mytickets/detail/views/TicketDetailLoader';
import BetbookChart from 'nike-ui/src/components/betbook-chart/BetbookChart';
import { accountUserData$, loggedIn$ } from '@/account/subs';
import BetbookPostReactions from '@/betbook/components/betbook-reactions/BetbookPostReactions';
import BetbookMatchExpiration from 'nike-ui/src/components/betbook-match-expiration/BetbookMatchExpiration';
import { marketStatsBetbookBets$ } from 'nike-ui/src/components/market-statistics/reframe/h&s';
import { ticketDetail$, ticketMinimal$ } from '@/mytickets/detail/subs';
import BetLocationContext, { betLocation, getModalLocation } from 'nike-ui/src/common/BetLocationContext';
import LogOutIcon from '@/icons/LogOutIcon';
import { betbookDetailRoute } from '@/betbook/utils';
import { createNikeBetbookPostShareURL } from 'nike-ui/src/common/util';
import isEditable from 'nike-ui/src/components/betbook/utils/isEditable';
import { POST_ANALYSIS, POST_TICKET, postTypeEnum2Url } from 'nike-ui/src/components/betbook/constants';
import { AnalysisBoxColorHeader } from 'nike-ui/src/components/tournaments/tournament-special-page/AnalysisBoxColorHeader';
import NuiDate from 'nike-ui/src/components/nui-date/NuiDate';
import NuiBox from 'nike-ui/src/components/nui-box/NuiBox';
import useBoxStore from '@/nikeone/hook/useBoxStore';
import { BETBOOK } from '@/nikeone/context/BoxOptionsContext';
import BetbookRewardProgressBar from 'nike-ui/src/components/betbook-reward-progress-bar/BetbookRewardProgressBar';
import { SvgIcon } from 'nike-ui/src/icons/SvgIcon';
import BetbookAnalysisShareIcon from '@/icons/BetbookAnalysisShareIcon';
import RouteLink from 'nike-ui/src/common/routing/RouteLink';
import Markdown from 'nike-ui/src/components/markdown/Markdown';
import CashinPositionContext from 'nike-ui/src/common/CashinPositionContext';
import { CashinPosition } from 'nike-ui/src/components/turboLive/components/cashin/constants';

const BOXSTORE = 'betbook';

export function BetbookPostItem({ postType, post, detail = false, isPromoPage = false }) {
    const { subscribe } = useReframe();
    const {
        id: postId,
        text,
        title,
        urlSuffix,
        creationDate,
        modificationDate,
        editableUntil,
        commentCount = 0,
        viewCount
    } = post || {};
    const { currentUserCommented, inspirationCount, author, pick, reward, ratings, ticket: postTicket } = post || {};
    const { betId, betSelectionCode, odds: initialOdds, matchId, market, tournament } = pick || {};
    const { status, value, maxValue, bonus, bonusPercent, statusReason } = reward || {};
    const { hash, stake } = postTicket || {};
    const sportId = postType === POST_ANALYSIS ? post?.pick?.sportId : post?.sportIds?.[0];

    useBoxStore(BOXSTORE, BETBOOK);
    const betLocationContext = useContext(BetLocationContext);
    const loggedIn = subscribe(loggedIn$);
    const { nickname: currentUserNickname } = subscribe(accountUserData$);
    const userPost = author?.nickname === currentUserNickname;
    const bet = subscribe(nike1Bet$(BOXSTORE, betId));
    const sportEvent = subscribe(nike1SportEvent$(BOXSTORE, bet?.sportEventId));
    const firstLetter = moment(creationDate)
        .fromNow()
        .charAt(0)
        .toUpperCase();
    const creationDateFormated = moment(creationDate)
        .fromNow()
        .slice(1);
    const ticket = subscribe(ticketDetail$(hash)) || subscribe(ticketMinimal$(hash));
    const marketStatsBetbookBets = subscribe(marketStatsBetbookBets$(matchId));
    const marketStatisticBet = marketStatsBetbookBets?.find(bet => bet.betId === betId);
    const filteredSelection = marketStatisticBet?.selections?.find(sel => sel.selection === betSelectionCode);
    const stakeShare = filteredSelection?.stakeShareGrouped || filteredSelection?.stakeShare;
    const modalContent = isPromoPage && detail;
    const showTitle = !isPromoPage || detail;
    const editable = userPost && isEditable(postType, creationDate, pick?.expiration, editableUntil);
    function openBetbookStatisticsModal(e) {
        e.stopPropagation();
        e.preventDefault();
        const sectionOrigin = getModalLocation(betLocationContext?.betLocationSection.id);
        PopupNg.dispatcher.open('betbook/statisticsDialog', { matchId, sportId, sportEvent, market, sectionOrigin }, 2);
    }

    function openRewardInfoFail(e) {
        e.stopPropagation();
        e.preventDefault();
        const title = '';
        const text = `bb.rewards.${statusReason}`;
        PopupNg.dispatcher.open('betbook/infoDialog', { title, text });
    }

    function replaceNewLines(text) {
        return text.replace(/\n/gm, '<br/>').replace(/\n+/gm, '<br/><br/>');
    }

    const element = (
        <NuiBox
            oldStyle={!isPromoPage}
            noPad
            otherClass={clsx('betbook-post-item', isPromoPage ? 'mb-10' : 'item-mb', {
                detail,
                'detail-modal': modalContent
            })}
            dataAtid="betbook-analysis-item"
            data-betbook-nickname={author?.nickname}
            data-betbook-type={postType}
            data-betbook-id={postId}
            data-betbook-title={title}
        >
            {isPromoPage && (
                <AnalysisBoxColorHeader bet={pick} tournamentName={tournament} expirationDate={pick?.expiration} />
            )}

            <div className={clsx({ 'px-15 py-15 mb-20': !detail, 'px-20 py-20': modalContent })}>
                {post && (
                    <>
                        <BetbookPostHeader
                            postType={postType}
                            post={post}
                            detail={detail}
                            loggedIn={loggedIn}
                            userPost={userPost}
                            hideOptions={isPromoPage || (userPost && !editable)}
                            editable={editable}
                        />

                        {detail ? (
                            <>
                                <BetbookPostReactions
                                    commentsCount={commentCount}
                                    customerComment={currentUserCommented}
                                    inspirationCount={inspirationCount}
                                    postType={postType}
                                    viewCount={viewCount}
                                    stake={stake}
                                />
                                <BetbookRewardProgressBar
                                    className="mb-30 mt-10"
                                    status={reward ? status : 'HIDDEN'}
                                    value={value}
                                    maxValue={maxValue}
                                    bonus={bonus}
                                    bonusPercent={bonusPercent}
                                >
                                    <button onClick={e => openRewardInfoFail(e)}>
                                        <SvgIcon
                                            imgId="info"
                                            className="c-black-40 info-icon"
                                            sizeX="16px"
                                            sizeY="16px"
                                        />
                                    </button>
                                </BetbookRewardProgressBar>
                            </>
                        ) : null}
                    </>
                )}
                {!isPromoPage && (
                    <>
                        <div
                            className={clsx('betbook-post-title flex fs-18', { detail })}
                            data-atid="betbook-post-title"
                        >
                            <div className="betbook-post-title-row">
                                <span className="title-specification">{getPostTitle(postType)}</span>
                                {title}
                            </div>
                        </div>
                        <div className="post-date" data-atid="betbook-post-date">
                            {postType === POST_ANALYSIS ? (
                                <BetbookMatchExpiration
                                    tournament={tournament}
                                    matchExpirationDate={pick?.expiration}
                                    otherClassNames="mt-5"
                                    sportId={sportId}
                                />
                            ) : null}
                        </div>
                    </>
                )}

                <div
                    className={clsx('post-text c-black-100', { 'line-clamp-3': !detail })}
                    data-atid="betbook-post-text"
                >
                    <NuiDate otherClass="betbook-date-space">
                        {firstLetter + creationDateFormated}
                        {modificationDate && ` • ${i18n('betbook.post.edited')}`}
                    </NuiDate>
                    {text ? <Markdown forceInline>{detail ? replaceNewLines(text) : text}</Markdown> : null}
                </div>
                {!showTitle && (
                    <div className="full-width text-center p-rel promo-cms-match-label lh-1 c-black-50 mt-15 mb-10">
                        {market && <span className="bg-white-box p-rel fs-12 px-10">{market}</span>}
                    </div>
                )}
                {betId && pick ? (
                    <>
                        <BetbookContextProvider
                            betbookPostId={postId}
                            betbookPostType={postType}
                            betbookPostAuthor={author.contractId}
                            betbookBetId={betId}
                            betbookBetSelectionCode={betSelectionCode}
                        >
                            <BetbookBetBox
                                bet={bet}
                                betId={betId}
                                betSelectionCode={betSelectionCode}
                                betSnapshot={pick}
                                match={sportEvent}
                                initialOdds={initialOdds}
                                showTitle={showTitle}
                                showSingleTip={!detail}
                            />
                        </BetbookContextProvider>
                        {stakeShare ? (
                            <BetbookChart
                                stakeShare={stakeShare}
                                onClick={openBetbookStatisticsModal}
                                overview={!detail}
                            />
                        ) : (
                            <div className="betbook-market-statistics-suspense" />
                        )}
                    </>
                ) : null}

                {hash && (postType === POST_TICKET || postType === POST_ANALYSIS) ? (
                    <BetbookPostItemTicket
                        ticket={ticket}
                        detail={detail}
                        isTicket={postType === POST_TICKET}
                        hash={hash}
                    />
                ) : null}

                {detail ? (
                    <BetbookDetailReactions
                        postType={postType}
                        ratings={ratings}
                        postId={postId}
                        postTitle={title}
                        authorNickName={author.nickname}
                        post={post}
                    />
                ) : (
                    <>
                        <BetbookPostReactions
                            commentsCount={commentCount}
                            customerComment={currentUserCommented}
                            inspirationCount={inspirationCount}
                            postType={postType}
                            stake={stake}
                            viewCount={viewCount}
                        />
                        <BetbookRewardProgressBar
                            className="mt-10"
                            status={reward ? status : 'HIDDEN'}
                            value={value}
                            maxValue={maxValue}
                            bonus={bonus}
                            bonusPercent={bonusPercent}
                        >
                            <button onClick={e => openRewardInfoFail(e)}>
                                <SvgIcon imgId="info" className="c-black-40 info-icon" sizeX="16px" sizeY="16px" />
                            </button>
                        </BetbookRewardProgressBar>
                    </>
                )}
            </div>
        </NuiBox>
    );
    return detail ? (
        element
    ) : (
        <RouteLink
            route={betbookDetailRoute(postType)}
            params={{ postId, postUrlSuffix: urlSuffix || '_' }}
            className="c-black"
        >
            {element}
        </RouteLink>
    );
}

function BetbookPostItemTicket({ ticket, detail, isTicket, hash }) {
    if (!ticket && detail) {
        return <TicketDetailLoader />;
    }
    if (!ticket || (ticket && ticket.error)) {
        return null;
    }
    if (detail && isTicket) {
        return (
            <BetLocationContext.Provider value={{ betLocationSection: betLocation.COMMUNITY_TICKET_DETAIL }}>
                <CashinPositionContext.Provider value={{ cashinPosition: CashinPosition.COMMUNITY_TICKET_DETAIL }}>
                    <TicketDetail ticket={fromJS(ticket)} />
                </CashinPositionContext.Provider>
            </BetLocationContext.Provider>
        );
    }
    if (isTicket || detail) {
        return (
            <BetbookPostTicket
                ticket={ticket}
                isAnalysis={!isTicket}
                hash={hash}
                label={detail ? i18n('betbook.ticket.label') : null}
            />
        );
    }

    return null;
}

function BetbookDetailReactions({ postId, postTitle, postType }) {
    return (
        <div className="betbook-detail-reactions">
            {postType === 'ANALYSIS' ? <ShareAnalysisButton analysisId={postId} /> : null}
            <SharedButton postType={postType} postId={postId} postTitle={postTitle} />
        </div>
    );
}

function SharedButton({ postTitle, postId, postType }) {
    const [active, setActive] = useState(false);

    function onClick(e) {
        if (!active) {
            sharePost(e, postId, postTitle, postType);
        }
        setActive(!active);
    }

    return (
        <div className={clsx('betbook-reaction-btn', { active })}>
            <Button
                data-atid="betbook-share-btn-facebook"
                className="betbook-reactions-option flex items-center fs-12 px-5 py-5 text-bold"
                onClick={onClick}
            >
                <LogOutIcon className="py-5 px-5" />
            </Button>
            <span className="betbook-reaction-text">
                {active ? i18n('common.shared.link') : i18n('common.share.link')}
            </span>
        </div>
    );
}

function ShareAnalysisButton({ analysisId }) {
    return (
        <div className="betbook-reaction-btn">
            <button
                data-atid="betbook-share-btn-analysis"
                className="betbook-reactions-option flex items-center fs-12 px-5 py-5 text-bold"
                onClick={() => dispatch(addAnalysisToChat(analysisId))}
            >
                <BetbookAnalysisShareIcon className="py-5 px-3" />
            </button>
            <span data-atid="bb-share-chat" className="betbook-reaction-btn">
                {i18n('markdown.analysis.share.btn')}
            </span>
        </div>
    );
}

function sharePost(e, postId, postTitle, postType) {
    e.preventDefault();
    e.stopPropagation();
    const baseUrl = getConfigProperty('hostUrl');

    if (navigator.share) {
        const navigate = createNikeBetbookPostShareURL(
            `${baseUrl}/betbook/${postTypeEnum2Url[postType]}/${postId}/${postTitle}`,
            postId,
            postType,
            postTitle,
            'navigator'
        );

        navigator.share({ url: navigate });
    } else {
        const appId = getConfigProperty('facebook.key');
        // natvrdo produkcna scrap url, nema vyznam tu davat testovacie hosty, ked sa facebook z vonka na ne aj tak nedostane
        const hashtag = encodeURIComponent('#NikeFandisNaplno');

        const href = createNikeBetbookPostShareURL(
            `https://www.nike.sk/betbook/${postTypeEnum2Url[postType]}/${postId}/${postTitle}`,
            postId,
            postType,
            postTitle,
            'facebook'
        );

        const url = `https://www.facebook.com/dialog/share?app_id=${appId}&display=touch&href=${encodeURIComponent(
            href
        )}&hashtag=${hashtag}`;
        window.open(url, '_blank');
    }
}

function BetbookPostHeader({ postType, post, detail, loggedIn, userPost, hideOptions, editable }) {
    const { id: postId } = post;
    const avatarId = post?.author?.avatarId;
    const nickname = post?.author?.nickname;

    function openOptionsDialog() {
        PopupNg.dispatcher.open('betbook/postOptions', { postType, postId, editable });
    }

    function openProfileDialog(e) {
        e.stopPropagation();
        e.preventDefault();
        fireGAEvent('Betbook Profile Dialog', 'Open');
        PopupNg.dispatcher.open('betbook/profileDialog', { avatarId, nickname });
    }

    return (
        <div className={clsx('betbook-post-header mb-10', { detail, 'pb-15': !detail })}>
            <div className="header left" onClick={openProfileDialog} data-atid="betbook-post-header-author">
                <Avatar avatarId={avatarId} />
                <div className={clsx('user-name', { userPost, detail })}>{nickname}</div>
            </div>
            {detail && loggedIn && !hideOptions ? (
                <div className="betbook-post-title-icon-more">
                    <BetbookOptionsButton onClick={openOptionsDialog} />
                </div>
            ) : null}
        </div>
    );
}

export const BetbookOptionsButton = ({ onClick }) => (
    <button onClick={onClick}>
        <div className="icon-more" />
    </button>
);

export default BetbookPostItem;
